import axios from 'axios';
import Constants from '../config/constants';

const API_BASE_URL = Constants.API_HOST;

// 타입 정의
export interface ReviewData {
  menu: string;
  reviewText: string;
  keywords: string[];
  beforeImageUrl?: string;
  afterImageUrl?: string;
  createdAt?: string;
}

export interface ReviewListParams {
  branchId: number;
  page?: number;
  limit?: number;
  serviceType?: string;
  dateRange?: string;
  searchText?: string;
}

export interface ReviewResponse {
  success: boolean;
  data: Review[];
  total: number;
  page: number;
  limit: number;
}

export interface Review {
  id: number;
  serviceType?: string;
  menu?: string;
  reviewText?: string;
  selectedOptions?: string[];
  branchId: number;
  beforeImageUrl?: string;
  afterImageUrl?: string;
  customerName?: string;
  customerPhone?: string;
  createdAt: string;
  updatedAt: string;
  isPosted?: boolean;
  postedAt?: string;
  postedPlatforms?: string[];
}

export interface ReviewStats {
  totalCount: number;
  positiveCount: number;
}

// GPT 프롬포트 관련 타입
export interface GPTPrompt {
  id: string;
  type: 'diary' | 'promotion';
  title: string;
  content: string;
  isActive: boolean;
  branchId: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface GPTPromptRequest {
  type: 'diary' | 'promotion';
  title: string;
  content: string;
  isActive: boolean;
}

export interface GPTGenerateRequest {
  promptId: string;
  reviewContent: string;
  serviceType?: string;
  images?: string[];
}

export interface GPTGenerateResponse {
  generatedText: string;
  usedPrompt: string;
}

// 백엔드 API 응답 타입
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// 리뷰 생성 API
export const apiCreateReview = async (reviewData: ReviewData) => {
  try {
    const response = await axios.post<ApiResponse<Review>>(
      `${API_BASE_URL}/api/app/review`,
      reviewData,
    );
    return response.data;
  } catch (error) {
    console.error('리뷰 생성 API 오류:', error);
    throw error;
  }
};

// 리뷰 목록 조회 API (지점별)
export const apiGetReviews = async (
  params: ReviewListParams,
): Promise<ReviewResponse> => {
  try {
    const response = await axios.get<ReviewResponse>(
      `${API_BASE_URL}/api/app/review`,
      {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error('리뷰 조회 API 오류:', error);
    throw error;
  }
};

// 리뷰 상세 조회 API
export const apiGetReview = async (reviewId: number): Promise<Review> => {
  try {
    const response = await axios.get<ApiResponse<Review>>(
      `${API_BASE_URL}/api/app/review/${reviewId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error('리뷰 상세 조회 API 오류:', error);
    throw error;
  }
};

// 리뷰 삭제 API
export const apiDeleteReview = async (reviewId: number) => {
  try {
    const response = await axios.delete<ApiResponse<null>>(
      `${API_BASE_URL}/api/app/review/${reviewId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error('리뷰 삭제 API 오류:', error);
    throw error;
  }
};

// 리뷰 통계 조회 API
export const apiGetReviewStats = async (
  branchId: number,
): Promise<ReviewStats> => {
  try {
    const response = await axios.get<ApiResponse<ReviewStats>>(
      `${API_BASE_URL}/api/app/review/stats`,
      {
        params: { branchId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error('리뷰 통계 조회 API 오류:', error);
    throw error;
  }
};

// 이미지 업로드 API
export const apiUploadImage = async (file: File): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post<ApiResponse<{ imageUrl: string }>>(
      `${API_BASE_URL}/api/app/review/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data.data.imageUrl;
  } catch (error) {
    console.error('이미지 업로드 API 오류:', error);
    throw error;
  }
};
